<template>
  <header class="app-background shadow-md p-4 flex justify-between items-center">
    <!-- Modals -->
    <div v-if="showCustomerListModal" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white p-6 rounded-lg shadow-lg w-full">
        <CustomersPage :isModal="true" @close="showCustomerListModal = false" />
      </div>
    </div>
    <!-- Mobile Menu Button -->
    <button
  @click="$emit('toggle-sidebar')"
  class="text-gray-600 hover:text-gray-900 md:hidden px-2" 
  aria-label="Toggle menu"
>
  <MenuIcon class="w-6 h-6" />
</button>

    <!-- Search Bar -->
    <div class="relative w-full max-w-md">
      <!-- <input type="text" placeholder="Search..."
        class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300" /> -->
    </div>

    <!-- Right Side: Calculator & Profile -->
    <div class="flex items-center space-x-4">
      <!-- Quick Access (Smaller Buttons) -->
      <button @click="showCustomerListModal = true" type="button" title="Customers"
        class="bg-theme-2 text-white px-3 py-1.5 rounded-md hover:bg-theme-primary flex items-center gap-1 text-sm">
        <Users class="w-4 h-4" />
        <!-- <span>Customers</span> -->
      </button>

      <router-link :to="{ name: 'NewPurchase', query: { redirect: $route.fullPath } }"
        title="Purchase"
        class="bg-theme-2 text-white px-3 py-1.5 rounded-md hover:bg-theme-primary flex items-center gap-1 text-sm">
        <ShoppingBag class="w-4 h-4" />
        <!-- <span>Purchase</span> -->
      </router-link>

      <router-link :to="{ name: 'NewOrder', query: { redirect: $route.fullPath } }"
      title="POS"
        class="bg-theme-2 text-white px-3 py-1.5 rounded-md hover:bg-theme-primary flex items-center gap-1 text-sm">
        <ShoppingCart class="w-4 h-4" />
        <!-- <span>POS</span> -->
      </router-link>
      <button @click="toggleCalculator" class="bg-theme-2 text-white px-3 py-1.5 rounded-md hover:bg-theme-primary flex items-center gap-1 text-sm">
        <span class="w-4 h-4">
          <CalculatorIcon />
        </span>
      </button>

      <!-- Profile Dropdown -->
      <div class="relative" ref="dropdown">
        <button @click="toggleDropdown" class="flex items-center space-x-2 focus:outline-none">
          <img src="/profile.png" alt="User" class="w-10 h-10 rounded-full" />
          <span class="hidden md:block text-gray-700">Super Admin User</span>
        </button>
        <!-- Dropdown Menu -->
        <div v-if="isDropdownOpen" class="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-10">
          <router-link to="/users/profile" class="block w-full px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer"
            @click="closeDropdown">
            Profile
          </router-link>
          <a href="#" class="block w-full px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer"
            @click="closeDropdown">Settings</a>
          <a href="#" class="block w-full px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer"
            @click.prevent="handleLogoutUser">
            Logout
          </a>
        </div>
      </div>
    </div>
  </header>

  <!-- Calculator Modal -->
  <div v-if="showCalculator" ref="calculator"
    class="fixed top-20 left-20 bg-white shadow-lg rounded-lg p-4 w-64 cursor-move" @mousedown="startDrag"
    @mouseup="stopDrag" @mousemove="drag">
    <div class="flex justify-between items-center border-b pb-2 mb-2">
      <h3 class="text-lg font-bold">Calculator</h3>
      <button @click="toggleCalculator" class="text-red-500">&times;</button>
    </div>
    <input type="text" v-model="expression" class="w-full border p-2 mb-2 text-right" readonly />
    <div class="grid grid-cols-4 gap-2">
      <button v-for="btn in buttons" :key="btn" class="p-2 bg-gray-200 rounded hover:bg-gray-300"
        @click="pressButton(btn)">
        {{ btn }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import {
  Menu as MenuIcon,
  Calculator as CalculatorIcon,
  // SquarePercent,
  // LucideUserPlus,
  // User,
  // ShoppingBag,
  ShoppingCart,
  ShoppingBag,
  Users
} from "lucide-vue-next";
import CustomersPage from "@/pages/crm/CustomersPage.vue";
import { mapActions } from "vuex";
import { useToast } from "vue-toastification";

export default {
  components: {
    MenuIcon, CalculatorIcon, 
    // SquarePercent, 
    // LucideUserPlus, User,
    // ShoppingBag,
    ShoppingCart,
    ShoppingBag,
    Users,
    CustomersPage,
  },
  data() {
    return {
      showCustomerListModal: false,
    }
  },
  setup() {
    const isDropdownOpen = ref(false);
    const showCalculator = ref(false);
    const expression = ref("");
    const dropdown = ref(null);
    const calculator = ref(null);

    const buttons = ["7", "8", "9", "/", "4", "5", "6", "*", "1", "2", "3", "-", "0", "C", "=", "+"];

    // Profile Dropdown Functions
    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };
    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };
    

    // Calculator Functions
    const toggleCalculator = () => {
      showCalculator.value = !showCalculator.value;
    };
    const pressButton = (btn) => {
      if (btn === "=") {
        try {
          expression.value = eval(expression.value).toString();
        } catch {
          expression.value = "Error";
        }
      } else if (btn === "C") {
        expression.value = "";
      } else {
        expression.value += btn;
      }
    };

    // Click Outside to Close Dropdown
    const handleClickOutside = (event) => {
      if (dropdown.value && !dropdown.value.contains(event.target)) {
        closeDropdown();
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    // Drag Functionality for Calculator
    let offsetX = 0, offsetY = 0, isDragging = false;

    const startDrag = (event) => {
      isDragging = true;
      offsetX = event.clientX - calculator.value.getBoundingClientRect().left;
      offsetY = event.clientY - calculator.value.getBoundingClientRect().top;
    };

    const drag = (event) => {
      if (isDragging) {
        calculator.value.style.left = `${event.clientX - offsetX}px`;
        calculator.value.style.top = `${event.clientY - offsetY}px`;
      }
    };

    const stopDrag = () => {
      isDragging = false;
    };

    return {
      isDropdownOpen,
      showCalculator,
      toggleDropdown,
      closeDropdown,
      toggleCalculator,
      expression,
      buttons,
      pressButton,
      calculator,
      startDrag,
      drag,
      stopDrag
    };
  },
  methods: {
    ...mapActions(["logout"]),
    async handleLogoutUser() {
      await this.logout();
      const toast = useToast();
      toast.info("You have successfully logged out");
      this.$router.push({ name: "Login", query: { redirect: this.$route.fullPath } });
      this.closeDropdown();
    },
  },
};
</script>

<style scoped>
/* Make sure the calculator modal is draggable */
.fixed {
  position: fixed;
}
</style>
